import type { FC, ReactNode, Ref } from 'react'
import type { LucideIcon } from 'lucide-react'

import { useEffect, useRef, useState } from 'react'
import Link from 'next/link'

import {
    CalendarCheck,
    CircleCheck,
    Info,
    LayoutPanelTop,
    ListChecks,
    Magnet,
    Pencil,
    Plus,
    Printer,
    Repeat,
    Scissors,
    Trash2
} from 'lucide-react'

import { cn } from '@/lib/utils'
import { printArea, removePrintFrames } from '@/lib/print-area'

import { siteConfig } from '@/constants/site'

// Gollwitzer and Sheeran (2006, Advances in Experimental Social Psychology):
// if-then plans ("If situation Y is encountered, then I will initiate
// goal-directed behavior X!") had "a positive effect of medium-to-large
// magnitude (d= .65) on goal attainment" across 94 independent tests.
// Lally et al. (2010, European Journal of Social Psychology): a daily behavior
// in a consistent context took 18 to 254 days to feel automatic, median 66,
// and missing one opportunity did not materially affect the process.
const MEDIAN_DAYS = 66
const RANGE_DAYS = '18 to 254'

// The most routines kept at once: what fits on one printed sheet. A layout
// limit, not a health claim.
const MAX_ROUTINES = 6
const MAX_LENGTH = 80
const DEFAULT_TITLE = 'My daily routines'

// Routines are remembered in this browser only (localStorage), read after
// mount. Bump the version if the saved shape changes.
const STORE_KEY = 'wpf-habit-routines'
const STORE_VERSION = 1

const SITE_HOST = siteConfig.url.replace(/^https?:\/\//, '').replace(/\/$/, '')
const TRACKER_DAYS = Array.from({ length: MEDIAN_DAYS }, (_, index) => index + 1)

interface Draft {
    anchor: string
    habit: string
    place: string
    obstacle: string
    backup: string
}

interface Routine extends Draft {
    id: string
}

type DesignId = 'poster' | 'tracker' | 'cards'
type ThemeId = 'sage' | 'sunrise' | 'ocean' | 'ink'

interface SavedState {
    version: number
    routines: Routine[]
    design: DesignId
    theme: ThemeId
    title: string
}

interface Theme {
    id: ThemeId
    label: string
    swatch: string
    band: string
    badge: string
    soft: string
    accent: string
    line: string
    rule: string
    box: string
    goal: string
}

const EMPTY: Draft = { anchor: '', habit: '', place: '', obstacle: '', backup: '' }

const EXAMPLES: Draft[] = [
    { habit: 'walk for 10 minutes', anchor: 'finish lunch', place: 'around the block', obstacle: 'it is raining', backup: 'walk up and down the stairs for 5 minutes' },
    { habit: 'stretch for 5 minutes', anchor: 'switch off my laptop for the day', place: 'in the living room', obstacle: 'I feel too tired', backup: 'do one stretch for each side' },
    { habit: 'read 2 pages', anchor: 'get into bed', place: 'with the phone in another room', obstacle: 'I am out late', backup: 'read one page' },
    { habit: 'drink a glass of water', anchor: 'pour my morning coffee', place: 'in the kitchen', obstacle: 'I am in a rush', backup: 'take a bottle with me' }
]

const DESIGNS: { id: DesignId; label: string; description: string; icon: LucideIcon }[] = [
    { id: 'poster', label: 'Poster', description: 'All your routines on one bold sheet', icon: LayoutPanelTop },
    { id: 'tracker', label: '66-day tracker', description: 'A box to tick for each day', icon: CalendarCheck },
    { id: 'cards', label: 'Cut-out cards', description: 'One card for each spot at home', icon: Scissors }
]

const THEMES: Theme[] = [
    {
        id: 'sage',
        label: 'Sage',
        swatch: 'bg-holistic',
        band: 'bg-holistic text-white',
        badge: 'bg-holistic text-white',
        soft: 'bg-holistic-soft',
        accent: 'text-holistic',
        line: 'border-holistic/60',
        rule: 'border-holistic/25',
        box: 'border-holistic/45',
        goal: 'border-2 border-holistic bg-holistic-soft font-bold text-holistic'
    },
    {
        id: 'sunrise',
        label: 'Sunrise',
        swatch: 'bg-lifestyle',
        band: 'bg-lifestyle text-white',
        badge: 'bg-lifestyle text-white',
        soft: 'bg-lifestyle-soft',
        accent: 'text-lifestyle',
        line: 'border-lifestyle/60',
        rule: 'border-lifestyle/25',
        box: 'border-lifestyle/45',
        goal: 'border-2 border-lifestyle bg-lifestyle-soft font-bold text-lifestyle'
    },
    {
        id: 'ocean',
        label: 'Ocean',
        swatch: 'bg-mind',
        band: 'bg-mind text-white',
        badge: 'bg-mind text-white',
        soft: 'bg-mind-soft',
        accent: 'text-mind',
        line: 'border-mind/60',
        rule: 'border-mind/25',
        box: 'border-mind/45',
        goal: 'border-2 border-mind bg-mind-soft font-bold text-mind'
    },
    {
        id: 'ink',
        label: 'Ink saver',
        swatch: 'border-2 border-foreground bg-white',
        band: 'border-b-2 border-foreground bg-white text-foreground',
        badge: 'border-2 border-foreground bg-white text-foreground',
        soft: 'border border-foreground/30 bg-white',
        accent: 'text-foreground',
        line: 'border-foreground/60',
        rule: 'border-foreground/25',
        box: 'border-foreground/45',
        goal: 'border-2 border-foreground bg-white font-bold text-foreground'
    }
]

const inputClass =
    'mt-1 w-full rounded-lg border border-border bg-background px-3 py-2 text-base text-foreground outline-none placeholder:text-muted-foreground/70 focus-visible:ring-2 focus-visible:ring-ring'

const primaryButton =
    'inline-flex items-center gap-2 rounded-full bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground transition-colors hover:bg-primary/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50'

const secondaryButton =
    'inline-flex items-center gap-2 rounded-full border border-border bg-background px-5 py-2.5 text-sm font-semibold text-foreground transition-colors hover:bg-secondary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50'

const clean = (value: string): string => value.trim().replace(/[.!]+$/, '')

const cleanDraft = (draft: Draft): Draft => ({
    anchor: clean(draft.anchor),
    habit: clean(draft.habit),
    place: clean(draft.place),
    obstacle: clean(draft.obstacle),
    backup: clean(draft.backup)
})

// "walk for 10 minutes around the block"
const actionText = (draft: Draft): string => `${clean(draft.habit)}${clean(draft.place) ? ` ${clean(draft.place)}` : ''}`

const planLine = (draft: Draft): string => `After I ${clean(draft.anchor)}, I will ${actionText(draft)}.`

const backupLine = (draft: Draft): string | null =>
    clean(draft.obstacle) && clean(draft.backup) ? `If ${clean(draft.obstacle)}, then I will ${clean(draft.backup)}.` : null

const makeId = (): string => `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 8)}`

const readSaved = (): SavedState | null => {
    try {
        const raw = window.localStorage.getItem(STORE_KEY)
        if (!raw) return null
        const parsed = JSON.parse(raw) as Partial<SavedState>
        if (parsed.version !== STORE_VERSION || !Array.isArray(parsed.routines)) return null

        const text = (value: unknown): string => (typeof value === 'string' ? value.slice(0, MAX_LENGTH) : '')
        const routines = parsed.routines
            .map((item: unknown): Routine => {
                const entry = (item && typeof item === 'object' ? item : {}) as Record<string, unknown>
                return {
                    id: text(entry.id) || makeId(),
                    anchor: text(entry.anchor),
                    habit: text(entry.habit),
                    place: text(entry.place),
                    obstacle: text(entry.obstacle),
                    backup: text(entry.backup)
                }
            })
            .filter((routine) => clean(routine.anchor) && clean(routine.habit))
            .slice(0, MAX_ROUTINES)

        return {
            version: STORE_VERSION,
            routines,
            design: DESIGNS.some((item) => item.id === parsed.design) ? (parsed.design as DesignId) : 'poster',
            theme: THEMES.some((item) => item.id === parsed.theme) ? (parsed.theme as ThemeId) : 'sage',
            title: text(parsed.title) || DEFAULT_TITLE
        }
    } catch {
        return null
    }
}

const writeSaved = (state: SavedState | null): void => {
    try {
        if (state) window.localStorage.setItem(STORE_KEY, JSON.stringify(state))
        else window.localStorage.removeItem(STORE_KEY)
    } catch {
        // Storage full or blocked: the planner still works, it just will not be remembered.
    }
}

interface FieldProps {
    id: string
    label: string
    prefix: string
    value: string
    onChange: (value: string) => void
    placeholder: string
    hint?: string
    inputRef?: Ref<HTMLInputElement>
}

const Field: FC<FieldProps> = ({ id, label, prefix, value, onChange, placeholder, hint, inputRef }): ReactNode => (
    <div>
        <label htmlFor={id} className='block text-xs font-medium text-muted-foreground'>{label}</label>
        <div className='mt-1 flex items-center gap-2'>
            <span className='shrink-0 text-sm text-foreground'>{prefix}</span>
            <input
                id={id}
                ref={inputRef}
                value={value}
                maxLength={MAX_LENGTH}
                placeholder={placeholder}
                onChange={(event) => onChange(event.target.value)}
                className={cn(inputClass, 'mt-0')}
            />
        </div>
        {hint && <span className='mt-1 block text-xs text-muted-foreground'>{hint}</span>}
    </div>
)

// ---------------------------------------------------------------------------
// Fridge sheets
// ---------------------------------------------------------------------------

interface SheetProps {
    routines: Routine[]
    theme: Theme
    title: string
}

const SunMark: FC = (): ReactNode => (
    <svg
        aria-hidden='true'
        viewBox='0 0 200 110'
        fill='none'
        stroke='currentColor'
        strokeWidth='9'
        className='pointer-events-none absolute -right-4 -bottom-3 h-28 w-52 opacity-25 sm:h-32 sm:w-60'
    >
        <circle cx='100' cy='110' r='30' fill='currentColor' stroke='none' />
        <path d='M45 110a55 55 0 0 1 110 0' />
        <path d='M14 110a86 86 0 0 1 172 0' />
    </svg>
)

const PosterSheet: FC<SheetProps> = ({ routines, theme, title }): ReactNode => (
    <div className='flex flex-col print:min-h-[240mm]'>
        <div className={cn('relative overflow-hidden px-6 py-7 sm:px-8', theme.band)}>
            <SunMark />
            <span className='relative block text-xs font-semibold uppercase tracking-[0.2em] opacity-85'>Every day</span>
            <span className='relative mt-1 block font-heading text-3xl font-semibold leading-tight sm:text-4xl'>{title}</span>
        </div>

        <div className='grid gap-3 px-5 py-6 sm:px-8'>
            {routines.map((routine, index) => (
                <div key={routine.id} className={cn('flex gap-4 rounded-xl px-4 py-4 break-inside-avoid', theme.soft)}>
                    <span className={cn('flex size-9 shrink-0 items-center justify-center rounded-full font-heading text-lg font-semibold', theme.badge)}>
                        {index + 1}
                    </span>
                    <span className='min-w-0 flex-1'>
                        <span className={cn('block text-[11px] font-semibold uppercase tracking-[0.14em]', theme.accent)}>
                            After I {clean(routine.anchor)}
                        </span>
                        <span className='mt-1 block font-heading text-xl font-semibold leading-snug text-foreground'>
                            I will {actionText(routine)}
                        </span>
                        {backupLine(routine) && (
                            <span className='mt-2 block text-sm leading-relaxed text-foreground/80'>
                                <span className='font-semibold text-foreground'>Plan B:</span> if {clean(routine.obstacle)}, then I will {clean(routine.backup)}.
                            </span>
                        )}
                    </span>
                </div>
            ))}
        </div>

        <div className={cn('mt-auto flex flex-wrap items-end justify-between gap-3 border-t px-6 py-4 sm:px-8', theme.rule)}>
            <span className='max-w-md text-sm leading-relaxed text-foreground/80'>
                Missed a day? Just pick it up again. In one study, new habits took a median of {MEDIAN_DAYS} days to feel automatic.
            </span>
            <span className='text-xs text-muted-foreground'>{SITE_HOST}</span>
        </div>
    </div>
)

const TrackerSheet: FC<SheetProps> = ({ routines, theme, title }): ReactNode => (
    <div className='flex flex-col print:min-h-[240mm]'>
        <div className={cn('relative overflow-hidden px-6 py-5 sm:px-8', theme.band)}>
            <span className='relative block font-heading text-2xl font-semibold leading-tight sm:text-3xl'>{title}</span>
            <span className='relative mt-1 block text-sm opacity-90'>
                Tick a box each day you do it. Missed one? Leave it blank and carry on.
            </span>
        </div>

        <div className='grid gap-5 px-5 py-5 sm:px-8'>
            {routines.map((routine, index) => (
                <div key={routine.id} className='break-inside-avoid'>
                    <span className='flex items-baseline gap-2'>
                        <span className={cn('font-heading text-lg font-semibold', theme.accent)}>{index + 1}</span>
                        <span className='text-sm font-semibold leading-snug text-foreground'>{planLine(routine)}</span>
                    </span>
                    <div className='mt-2 grid grid-cols-11 gap-1 sm:grid-cols-22 print:grid-cols-22'>
                        {TRACKER_DAYS.map((day) => (
                            <span
                                key={day}
                                className={cn(
                                    'flex aspect-square items-start justify-start rounded-[3px] border p-[2px] text-[8px] leading-none text-muted-foreground',
                                    theme.box,
                                    day === MEDIAN_DAYS && theme.goal
                                )}
                            >
                                {day}
                            </span>
                        ))}
                    </div>
                </div>
            ))}
        </div>

        <div className={cn('mt-auto flex flex-wrap items-end justify-between gap-3 border-t px-6 py-4 sm:px-8', theme.rule)}>
            <span className='max-w-md text-sm leading-relaxed text-foreground/80'>
                Day {MEDIAN_DAYS} was the median in one study, not a finish line: some habits took {RANGE_DAYS} days.
            </span>
            <span className='text-xs text-muted-foreground'>{SITE_HOST}</span>
        </div>
    </div>
)

const CardsSheet: FC<SheetProps> = ({ routines, theme, title }): ReactNode => (
    <div className='px-5 py-5 sm:px-6'>
        <span className='flex items-start gap-2 text-xs leading-relaxed text-muted-foreground'>
            <Scissors className='mt-0.5 size-4 shrink-0' />
            Cut along the dashed lines and put each card where the moment happens: by the kettle, on the mirror, by the door.
        </span>
        <div className='mt-4 grid gap-4 sm:grid-cols-2 print:grid-cols-2'>
            {routines.map((routine, index) => (
                <div key={routine.id} className={cn('rounded-xl border-2 border-dashed p-1.5 break-inside-avoid', theme.line)}>
                    <div className={cn('flex h-full flex-col rounded-lg px-4 py-4', theme.soft)}>
                        <span className={cn('flex items-center justify-between gap-2 text-[11px] font-semibold uppercase tracking-[0.14em]', theme.accent)}>
                            <span className='truncate'>{title}</span>
                            <span>{index + 1}</span>
                        </span>
                        <span className='mt-3 block text-sm text-foreground/80'>After I {clean(routine.anchor)},</span>
                        <span className='mt-1 mb-4 block font-heading text-xl font-semibold leading-snug text-foreground'>
                            I will {actionText(routine)}.
                        </span>
                        {backupLine(routine) && (
                            <span className={cn('mt-auto block border-t pt-3 text-sm leading-relaxed text-foreground/80', theme.rule)}>
                                <span className='font-semibold text-foreground'>Plan B:</span> if {clean(routine.obstacle)}, then I will {clean(routine.backup)}.
                            </span>
                        )}
                    </div>
                </div>
            ))}
        </div>
        <span className='mt-4 block text-right text-xs text-muted-foreground'>{SITE_HOST}</span>
    </div>
)

// ---------------------------------------------------------------------------
// Planner
// ---------------------------------------------------------------------------

export const HabitPlanner: FC = (): ReactNode => {
    const [draft, setDraft] = useState<Draft>(EXAMPLES[0])
    const [routines, setRoutines] = useState<Routine[]>([])
    const [view, setView] = useState<'form' | 'saved'>('form')
    const [editingId, setEditingId] = useState<string | null>(null)
    const [justSaved, setJustSaved] = useState(false)
    const [removed, setRemoved] = useState<{ routine: Routine; index: number } | null>(null)
    const [design, setDesign] = useState<DesignId>('poster')
    const [themeId, setThemeId] = useState<ThemeId>('sage')
    const [title, setTitle] = useState(DEFAULT_TITLE)
    const [hydrated, setHydrated] = useState(false)

    const printRef = useRef<HTMLDivElement>(null)
    const anchorRef = useRef<HTMLInputElement>(null)
    const statusRef = useRef<HTMLSpanElement>(null)
    const focusNext = useRef<'anchor' | 'status' | null>(null)

    useEffect(() => removePrintFrames, [])

    // Bring back routines saved in this browser, after mount so the server
    // and the first client render match.
    useEffect(() => {
        const saved = readSaved()
        if (saved && saved.routines.length > 0) {
            setRoutines(saved.routines)
            setDesign(saved.design)
            setThemeId(saved.theme)
            setTitle(saved.title)
            setView('saved')
        }
        setHydrated(true)
    }, [])

    useEffect(() => {
        if (!hydrated) return
        writeSaved(routines.length > 0 ? { version: STORE_VERSION, routines, design, theme: themeId, title } : null)
    }, [hydrated, routines, design, themeId, title])

    useEffect(() => {
        const target = focusNext.current
        focusNext.current = null
        if (target === 'anchor') anchorRef.current?.focus()
        if (target === 'status') statusRef.current?.focus()
    }, [view, editingId])

    const theme = THEMES.find((item) => item.id === themeId) ?? THEMES[0]
    const sheetTitle = title.trim() || DEFAULT_TITLE
    const ready = Boolean(clean(draft.anchor) && clean(draft.habit))
    const full = routines.length >= MAX_ROUTINES
    const editingIndex = editingId ? routines.findIndex((routine) => routine.id === editingId) : -1

    const update = (key: keyof Draft) => (value: string): void => setDraft((current) => ({ ...current, [key]: value }))

    const saveRoutine = (): void => {
        if (!ready) return
        const cleaned = cleanDraft(draft)

        if (editingId) {
            setRoutines((current) => current.map((routine) => (routine.id === editingId ? { ...cleaned, id: editingId } : routine)))
        } else {
            if (full) return
            setRoutines((current) => [...current, { ...cleaned, id: makeId() }])
        }

        setRemoved(null)
        setEditingId(null)
        setJustSaved(true)
        focusNext.current = 'status'
        setView('saved')
    }

    const createAnother = (): void => {
        if (full) return
        setDraft(EMPTY)
        setEditingId(null)
        setRemoved(null)
        focusNext.current = 'anchor'
        setView('form')
    }

    const editRoutine = (routine: Routine): void => {
        setDraft({ anchor: routine.anchor, habit: routine.habit, place: routine.place, obstacle: routine.obstacle, backup: routine.backup })
        setRemoved(null)
        focusNext.current = 'anchor'
        setEditingId(routine.id)
        setView('form')
    }

    const cancelForm = (): void => {
        setEditingId(null)
        setJustSaved(false)
        setView('saved')
    }

    const removeRoutine = (routine: Routine, index: number): void => {
        const remaining = routines.filter((item) => item.id !== routine.id)
        setRoutines(remaining)
        setRemoved({ routine, index })

        if (editingId === routine.id) {
            setEditingId(null)
            setDraft(EMPTY)
        }
        if (remaining.length === 0) {
            setEditingId(null)
            setView('form')
        }
    }

    const undoRemove = (): void => {
        if (!removed || routines.length >= MAX_ROUTINES) return
        const next = [...routines]
        next.splice(Math.min(removed.index, next.length), 0, removed.routine)
        setRoutines(next)
        setRemoved(null)
        if (routines.length === 0) {
            setJustSaved(false)
            setView('saved')
        }
    }

    const showSheets = (): void => {
        document.getElementById('habit-fridge-sheets')?.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }

    const applyExample = (index: number): void => setDraft(EXAMPLES[index])

    return (
        <section aria-label='Habit planner' className='my-10 overflow-hidden rounded-2xl border border-border bg-card shadow-sm'>
            <div className='border-b border-border bg-secondary/50 px-5 py-4 sm:px-6'>
                <div className='flex items-center gap-2'>
                    <Repeat className='size-5 shrink-0 text-primary' />
                    <span className='font-heading text-lg font-semibold tracking-tight text-foreground'>If-then habit planner</span>
                </div>
                <span className='mt-1 block text-sm text-muted-foreground'>
                    Turn a habit you want into a specific plan: when, where, and what to do if something gets in the way.
                    Save up to {MAX_ROUTINES} routines, then print them for your fridge.
                </span>
            </div>

            <div className='px-5 py-5 sm:px-6'>
                {view === 'saved' ? (
                    <div className='rounded-xl border border-primary/40 bg-primary/5 px-4 py-4'>
                        <span ref={statusRef} tabIndex={-1} className='flex items-center gap-2 font-heading text-lg font-semibold text-foreground outline-none'>
                            <CircleCheck className='size-5 shrink-0 text-primary' />
                            {justSaved ? 'Routine saved' : 'Your routines'}
                        </span>
                        <span className='mt-1 block text-sm leading-relaxed text-muted-foreground'>
                            {routines.length === 1 ? 'You have 1 routine.' : `You have ${routines.length} routines.`}{' '}
                            {full ? 'That is the most that fit on one sheet.' : 'Add another, or turn them into a sheet for your fridge.'}
                        </span>
                        <span className='mt-4 flex flex-wrap gap-3'>
                            <button type='button' onClick={createAnother} disabled={full} className={primaryButton}>
                                <Plus className='size-4' />
                                Create another routine
                            </button>
                            <button type='button' onClick={showSheets} className={secondaryButton}>
                                <Magnet className='size-4' />
                                Make a fridge sheet
                            </button>
                        </span>
                        {full && (
                            <span className='mt-3 block text-xs text-muted-foreground'>
                                To add another, remove one of your routines below.
                            </span>
                        )}
                    </div>
                ) : (
                    <div>
                        {(editingId || routines.length > 0) && (
                            <span className='mb-4 flex flex-wrap items-center justify-between gap-2 rounded-lg bg-secondary/60 px-3 py-2 text-sm font-medium text-foreground'>
                                {editingId ? `Editing routine ${editingIndex + 1}` : `New routine (${routines.length + 1} of up to ${MAX_ROUTINES})`}
                            </span>
                        )}

                        <span className='block text-xs font-medium text-muted-foreground'>Start from an example</span>
                        <div className='mt-2 flex flex-wrap gap-2'>
                            {EXAMPLES.map((example, index) => (
                                <button
                                    key={example.habit}
                                    type='button'
                                    onClick={() => applyExample(index)}
                                    className='rounded-full border border-border bg-background px-4 py-2 text-sm font-medium text-foreground transition-colors hover:bg-secondary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring'
                                >
                                    {example.habit.charAt(0).toUpperCase() + example.habit.slice(1)}
                                </button>
                            ))}
                        </div>

                        <div className='mt-5 grid gap-4'>
                            <Field
                                id='habit-anchor'
                                inputRef={anchorRef}
                                label='Something you already do every day'
                                prefix='After I'
                                value={draft.anchor}
                                onChange={update('anchor')}
                                placeholder='make my morning coffee'
                                hint='Pick a fixed moment, like making coffee or arriving home.'
                            />
                            <Field id='habit-action' label='The new habit, kept small' prefix='I will' value={draft.habit} onChange={update('habit')} placeholder='do 10 squats' hint='Small enough to do on a bad day.' />
                            <Field id='habit-place' label='Where (optional)' prefix='Where:' value={draft.place} onChange={update('place')} placeholder='in the kitchen' />
                            <Field id='habit-obstacle' label='What is most likely to get in the way?' prefix='If' value={draft.obstacle} onChange={update('obstacle')} placeholder='I am running late' />
                            <Field id='habit-backup' label='Your backup plan' prefix='then I will' value={draft.backup} onChange={update('backup')} placeholder='do 3 squats' />
                        </div>

                        <div aria-live='polite' className='mt-6'>
                            {!ready ? (
                                <span className='block rounded-xl border border-border bg-background px-4 py-4 text-sm text-muted-foreground'>
                                    Fill in the moment and the habit to see your plan.
                                </span>
                            ) : (
                                <div className='rounded-xl border border-primary/40 bg-primary/5 px-4 py-4'>
                                    <span className='flex items-center gap-2 text-sm font-medium text-foreground'>
                                        <ListChecks className='size-4 text-primary' />
                                        Your plan
                                    </span>
                                    <span className='mt-2 block font-heading text-2xl font-semibold leading-snug tracking-tight text-foreground'>
                                        {planLine(draft)}
                                    </span>
                                    {backupLine(draft) && (
                                        <span className='mt-2 block text-base text-foreground'>{backupLine(draft)}</span>
                                    )}
                                    <span className='mt-3 block text-sm leading-relaxed text-muted-foreground'>
                                        Expect it to take a while to feel automatic: in one study the median was {MEDIAN_DAYS} days, with a
                                        range of {RANGE_DAYS}. Missing a single day did not set people back, so just pick it up again.
                                    </span>
                                </div>
                            )}
                        </div>

                        <span className='mt-4 flex flex-wrap gap-3'>
                            <button type='button' onClick={saveRoutine} disabled={!ready || (!editingId && full)} className={primaryButton}>
                                <CircleCheck className='size-4' />
                                {editingId ? 'Save changes' : 'Save routine'}
                            </button>
                            {routines.length > 0 && (
                                <button type='button' onClick={cancelForm} className={secondaryButton}>
                                    Cancel
                                </button>
                            )}
                        </span>
                    </div>
                )}

                {removed && (
                    <span role='status' className='mt-5 flex flex-wrap items-center justify-between gap-2 rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground'>
                        Routine removed.
                        <button
                            type='button'
                            onClick={undoRemove}
                            className='rounded-full px-3 py-1 text-sm font-semibold text-primary hover:bg-secondary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring'
                        >
                            Undo
                        </button>
                    </span>
                )}

                {routines.length > 0 && (
                    <div className='mt-8'>
                        <span className='flex items-baseline justify-between gap-2'>
                            <span className='font-heading text-lg font-semibold tracking-tight text-foreground'>My routines</span>
                            <span className='text-xs text-muted-foreground'>{routines.length} of {MAX_ROUTINES}</span>
                        </span>
                        <div role='list' className='mt-3 grid gap-3'>
                            {routines.map((routine, index) => (
                                <div
                                    key={routine.id}
                                    role='listitem'
                                    className={cn(
                                        'flex gap-3 rounded-xl border border-border bg-background px-4 py-3',
                                        editingId === routine.id && 'border-primary ring-1 ring-primary'
                                    )}
                                >
                                    <span className='flex size-7 shrink-0 items-center justify-center rounded-full bg-primary text-xs font-semibold text-primary-foreground'>
                                        {index + 1}
                                    </span>
                                    <span className='min-w-0 flex-1'>
                                        <span className='block text-sm font-medium leading-snug text-foreground'>{planLine(routine)}</span>
                                        {backupLine(routine) && (
                                            <span className='mt-0.5 block text-sm leading-snug text-muted-foreground'>{backupLine(routine)}</span>
                                        )}
                                    </span>
                                    <span className='flex shrink-0 items-start gap-1'>
                                        <button
                                            type='button'
                                            onClick={() => editRoutine(routine)}
                                            aria-label={`Edit routine ${index + 1}`}
                                            className='flex size-9 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring'
                                        >
                                            <Pencil className='size-4' />
                                        </button>
                                        <button
                                            type='button'
                                            onClick={() => removeRoutine(routine, index)}
                                            aria-label={`Remove routine ${index + 1}`}
                                            className='flex size-9 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-secondary hover:text-destructive focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring'
                                        >
                                            <Trash2 className='size-4' />
                                        </button>
                                    </span>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {routines.length > 0 && (
                    <div id='habit-fridge-sheets' className='mt-8 scroll-mt-24 rounded-2xl border border-border bg-secondary/30 px-4 py-5 sm:px-5'>
                        <span className='flex items-center gap-2'>
                            <Magnet className='size-5 shrink-0 text-primary' />
                            <span className='font-heading text-lg font-semibold tracking-tight text-foreground'>Fridge sheets</span>
                        </span>
                        <span className='mt-1 block text-sm leading-relaxed text-muted-foreground'>
                            Pick a design and a color, then print it or save it as a PDF. Put it somewhere you look every day.
                        </span>

                        <span className='mt-5 block text-xs font-medium text-muted-foreground'>Design</span>
                        <div className='mt-2 grid gap-2 sm:grid-cols-3'>
                            {DESIGNS.map((item) => {
                                const Icon = item.icon
                                const active = design === item.id
                                return (
                                    <button
                                        key={item.id}
                                        type='button'
                                        aria-pressed={active}
                                        onClick={() => setDesign(item.id)}
                                        className={cn(
                                            'flex items-start gap-3 rounded-xl border bg-background px-3 py-3 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                                            active ? 'border-primary ring-1 ring-primary' : 'border-border hover:bg-secondary'
                                        )}
                                    >
                                        <Icon className={cn('mt-0.5 size-5 shrink-0', active ? 'text-primary' : 'text-muted-foreground')} />
                                        <span>
                                            <span className='block text-sm font-semibold text-foreground'>{item.label}</span>
                                            <span className='block text-xs leading-snug text-muted-foreground'>{item.description}</span>
                                        </span>
                                    </button>
                                )
                            })}
                        </div>

                        <span className='mt-5 block text-xs font-medium text-muted-foreground'>Color</span>
                        <div className='mt-2 flex flex-wrap gap-2'>
                            {THEMES.map((item) => {
                                const active = themeId === item.id
                                return (
                                    <button
                                        key={item.id}
                                        type='button'
                                        aria-pressed={active}
                                        onClick={() => setThemeId(item.id)}
                                        className={cn(
                                            'inline-flex items-center gap-2 rounded-full border bg-background px-4 py-2 text-sm font-medium text-foreground transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                                            active ? 'border-primary ring-1 ring-primary' : 'border-border hover:bg-secondary'
                                        )}
                                    >
                                        <span className={cn('size-4 rounded-full', item.swatch)} />
                                        {item.label}
                                    </button>
                                )
                            })}
                        </div>

                        <label htmlFor='habit-sheet-title' className='mt-5 block text-xs font-medium text-muted-foreground'>
                            Title at the top
                        </label>
                        <input
                            id='habit-sheet-title'
                            value={title}
                            maxLength={40}
                            placeholder={DEFAULT_TITLE}
                            onChange={(event) => setTitle(event.target.value)}
                            className={cn(inputClass, 'sm:max-w-sm')}
                        />

                        <div className='mt-5'>
                            <div
                                ref={printRef}
                                data-print-area
                                className='[-webkit-print-color-adjust:exact] [print-color-adjust:exact]'
                            >
                                <div className='overflow-hidden rounded-xl border border-border bg-white text-foreground shadow-sm print:overflow-visible print:rounded-none print:border-0 print:shadow-none'>
                                    {design === 'poster' && <PosterSheet routines={routines} theme={theme} title={sheetTitle} />}
                                    {design === 'tracker' && <TrackerSheet routines={routines} theme={theme} title={sheetTitle} />}
                                    {design === 'cards' && <CardsSheet routines={routines} theme={theme} title={sheetTitle} />}
                                </div>
                            </div>
                        </div>

                        <span className='mt-5 flex flex-wrap items-center gap-3'>
                            <button
                                type='button'
                                onClick={() => printRef.current && printArea(printRef.current, `${sheetTitle} | WellPath Fusion`)}
                                className={primaryButton}
                            >
                                <Printer className='size-4' />
                                Print or save as PDF
                            </button>
                            <span className='text-xs leading-relaxed text-muted-foreground'>
                                If the colors do not print, turn on Background graphics in the print options.
                            </span>
                        </span>
                    </div>
                )}

                <div className='mt-5 flex gap-2 rounded-xl border border-border bg-secondary/40 px-4 py-3'>
                    <Info className='mt-0.5 size-4 shrink-0 text-muted-foreground' />
                    <span className='text-xs leading-relaxed text-muted-foreground'>
                        Your routines are saved in this browser only, so they are still here when you come back. Nothing is sent
                        anywhere. For why plans like this work, see{' '}
                        <Link href='/energy-and-focus/how-motivation-works' className='font-normal text-inherit underline underline-offset-2 hover:text-foreground'>
                            how motivation works
                        </Link>
                        .
                    </span>
                </div>
            </div>
        </section>
    )
}

export default HabitPlanner
